import { useState, type FormEvent } from 'react';
import { TenantRoleSchema, type TenantRole } from '@endcopilot/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useResource } from '../lib/useResource';
import type { TenantUser } from '../lib/types';
import { ROLE_DESCRIPTION, ROLE_LABEL } from '../lib/labels';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  OneTimeSecret,
  PageHeader,
  Select,
  Table,
  errorMessage,
  fieldError,
} from '../components/ui';

function MfaResetButton({ user }: { user: TenantUser }) {
  const [confirming, setConfirming] = useState(false);
  const [state, setState] = useState<'idle' | 'busy' | 'done' | { error: string }>('idle');
  if (state === 'done') return <span className="text-xs text-emerald-700">MFA resetado</span>;
  if (!confirming) {
    return (
      <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setConfirming(true)}>
        Resetar MFA
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-slate-700">Encerrar sessões e exigir novo cadastro?</span>
      <Button
        variant="danger"
        className="px-2 py-1 text-xs"
        busy={state === 'busy'}
        onClick={async () => {
          setState('busy');
          try {
            await api.post(`/v1/admin/users/${user.id}/mfa-reset`, { confirm: true });
            setState('done');
          } catch (e) {
            setState({ error: errorMessage(e) });
          }
        }}
      >
        Confirmar
      </Button>
      <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setConfirming(false)}>
        Cancelar
      </Button>
      {typeof state === 'object' && <span className="text-red-600">{state.error}</span>}
    </div>
  );
}

export function UsersPage() {
  const { tenant, can, me } = useSession();
  const users = useResource(
    () => api.get<TenantUser[]>('/v1/users', { tenant: true }),
    [tenant?.id],
  );
  const [form, setForm] = useState({ email: '', name: '', role: 'VIEWER' as TenantRole });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ email: string; temporaryPassword: string | null } | null>(
    null,
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post<{ email: string; temporaryPassword: string | null }>(
        '/v1/users',
        form,
        { tenant: true },
      );
      setResult(r);
      setForm({ email: '', name: '', role: 'VIEWER' });
      await users.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Usuários"
        description={`Pessoas com acesso ao painel de ${tenant?.name ?? 'empresa'}.`}
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <Card title="Usuários da empresa">
          {users.error != null && <Alert tone="error">{errorMessage(users.error)}</Alert>}
          <Table
            head={['Nome', 'E-mail', 'Papel', 'Situação', ...(me?.user.isSuperadmin ? [''] : [])]}
            empty={
              !users.loading && users.data?.length === 0 && <EmptyState>Nenhum usuário.</EmptyState>
            }
          >
            {users.data?.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2 font-medium text-slate-900">{u.name}</td>
                <td className="px-4 py-2 text-slate-600">{u.email}</td>
                <td className="px-4 py-2">
                  <Badge tone={u.role === 'TENANT_ADMIN' ? 'indigo' : 'slate'}>
                    {ROLE_LABEL[u.role]}
                  </Badge>
                </td>
                <td className="px-4 py-2">
                  {u.status === 'ACTIVE' ? (
                    <Badge tone="emerald">Ativo</Badge>
                  ) : (
                    <Badge tone="red">Desativado</Badge>
                  )}
                </td>
                {me?.user.isSuperadmin && (
                  <td className="px-4 py-2 text-right">
                    {u.id !== me.user.id && <MfaResetButton user={u} />}
                  </td>
                )}
              </tr>
            ))}
          </Table>
          {users.loading && <p className="pt-3 text-sm text-slate-500">Carregando…</p>}
        </Card>

        {can('users:manage') && (
          <Card title="Novo usuário">
            <form onSubmit={submit} className="space-y-4" noValidate>
              {error != null && <Alert tone="error">{errorMessage(error)}</Alert>}
              {result &&
                (result.temporaryPassword ? (
                  <OneTimeSecret
                    label={`Senha temporária de ${result.email}`}
                    value={result.temporaryPassword}
                    note="Exibida só agora. Envie por um canal seguro. No primeiro acesso a pessoa define a própria senha."
                  />
                ) : (
                  <Alert tone="success">
                    {result.email} já tinha conta e foi vinculado a esta empresa com a senha atual.
                  </Alert>
                ))}
              <Input
                label="Nome"
                value={form.name}
                error={fieldError(error, 'name')}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                label="E-mail"
                type="email"
                value={form.email}
                error={fieldError(error, 'email')}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <Select
                label="Papel"
                value={form.role}
                hint={ROLE_DESCRIPTION[form.role]}
                onChange={(e) => setForm({ ...form, role: e.target.value as TenantRole })}
              >
                {TenantRoleSchema.options.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
              <Button
                type="submit"
                busy={busy}
                disabled={!form.email || form.name.trim().length < 2}
              >
                Cadastrar usuário
              </Button>
            </form>
          </Card>
        )}
      </div>
    </>
  );
}
